import type { IntakeTableColumnInput } from '@common/utils/intakeFieldFormatting';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import {
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import EditableViewComponentLeftAligned from '@/components/ReadOnlyComponent/EditableViewComponentLeftAligned';
import RenderCriticalQuestion from '@/components/RenderCriticalQuestion';

interface Props {
    title: string | null;
    isCriticalQuestion?: boolean;
    columns: IntakeTableColumnInput[];
    rows: string[][];
}

export default function IntakeResponseTable({
    title,
    isCriticalQuestion = false,
    columns,
    rows,
}: Props) {
    if (columns.length === 0) {
        return null;
    }

    if (rows.length === 0) {
        return (
            <EditableViewComponentLeftAligned
                label={<RenderCriticalQuestion title={title} isCritical={isCriticalQuestion} />}
                value=""
                isCopyable={false}
            />
        );
    }

    return (
        <Stack gap="8px" width="100%">
            <Typography variant="labelLarge">
                <RenderCriticalQuestion title={title} isCritical={isCriticalQuestion} />
            </Typography>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                    <TableRow>
                        {columns.map((column, columnIndex) => (
                            <TableCell
                                key={column.key ?? columnIndex}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'neutral.borderColor',
                                    color: 'neutral.N_500',
                                    fontWeight: 700,
                                }}
                            >
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {columns.map((column, columnIndex) => {
                                const cellValue = row[columnIndex] || '';

                                return (
                                    <TableCell
                                        key={column.key ?? columnIndex}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'neutral.borderColor',
                                            verticalAlign: 'top',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="flex-start"
                                            justifyContent="space-between"
                                            gap="8px"
                                        >
                                            <Typography variant="h4" sx={{ fontWeight: 500 }}>
                                                {cellValue}
                                            </Typography>
                                            {!!cellValue && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        navigator.clipboard.writeText(cellValue)
                                                    }
                                                    sx={{ padding: 0 }}
                                                >
                                                    <ContentCopyOutlinedIcon
                                                        sx={{ fontSize: 16 }}
                                                    />
                                                </IconButton>
                                            )}
                                        </Stack>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Stack>
    );
}
